import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Loader from '../../common/Loader';
import Button from '../../Button';
import Payments from './Payments';
import EmptyState from './EmptyState';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import OrganizationPopup from './OrganizationPopup';
import PaymentMethodsHint from './PaymentMethodsHint';
import EmptyOrganizationsState from './EmptyOrganizationsState';

import { usePageView } from '../../../hocs/usePageView';
import useIsLoggedInUser from '../../../hooks/useIsLoggedInUser';
import { actions as paymentsActions } from '../../../redux/payment-methods/requests/payments-req';
import {
  targetUserIdSelector,
  selectTargetUserOrganizations,
} from '../../../redux/context/selectors';
import {
  selectPaymentList,
  selectIsPaymentListFetching,
} from '../../../redux/payment-methods/selectors';

import styles from './index.styl';

const ProfilePaymentMethods = () => {
  const dispatch = useDispatch();

  usePageView('Profile | Payment Methods');

  const [opened, setOpened] = useState(false);

  const { formatMessage } = useIntl();

  const isLoggedInUser = useIsLoggedInUser();

  const paymentList = useSelector(selectPaymentList);

  const isPaymentListFetching = useSelector(selectIsPaymentListFetching);

  const userId = useSelector(targetUserIdSelector);

  const userOrganizationsList = useSelector(selectTargetUserOrganizations);

  const addPaymentMethodTitle = formatMessage({
    id: 'profile.payment.addPaymentMethod',
  });

  const description = formatMessage({ id: 'profile.payment.description' });

  const paymentMethodsTitle = formatMessage({
    id: 'profile.payment.paymentMethods',
  });

  const togglePopup = () => setOpened(state => !state);

  const isEmpty = useMemo(
    () => !Array.isArray(paymentList) || paymentList.length === 0,
    [paymentList],
  );

  const paymentContent = useMemo(() => {
    if (isPaymentListFetching) return <Loader />;

    if (isEmpty) return <EmptyState />;

    return <Payments />;
  }, [isPaymentListFetching, isEmpty]);

  useEffect(() => {
    dispatch(paymentsActions.clear());

    if (!userId || !userOrganizationsList.length) return;

    dispatch(paymentsActions.send({ userId }));
  }, [dispatch, userId, userOrganizationsList.length]);

  return (
    <div
      className={cn(styles.container, {
        [styles.containerWithoutOrganizations]: !userOrganizationsList.length,
      })}
    >
      <MetaHelmet title="pages.title.profile.payment" />

      {userOrganizationsList.length ? (
        <div className={styles.content} aria-busy={isPaymentListFetching}>
          <div className={styles.header}>
            <div>
              <div className={styles.title}>
                {paymentMethodsTitle}

                {isLoggedInUser && <PaymentMethodsHint />}
              </div>
              {isLoggedInUser && (
                <div className={styles.description}>{description}</div>
              )}
            </div>

            {isLoggedInUser && (
              <Button raised color="primary" onClick={togglePopup}>
                {addPaymentMethodTitle}
              </Button>
            )}
            <OrganizationPopup opened={opened} onClose={togglePopup} />
          </div>

          {paymentContent}
        </div>
      ) : (
        <div className={styles.withoutOrganizations}>
          <EmptyOrganizationsState />
        </div>
      )}
    </div>
  );
};

export default ProfilePaymentMethods;



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/ProfilePaymentMethods/index.jsx